import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getAiClientActivity } from './myPageApi';
import { formatDate, statusLabels, toActivities } from './aiClientActivity';
import ChatTranscript from './ChatTranscript';

// 활동 목록에서 요약 지표(완료한 면담 수, 총 면담 시간, 만난 내담자 수)를 계산한다.
function summarize(activities) {
    const completed = activities.filter(item => item.status === 'completed');
    return [
        { label: '완료한 면담', value: completed.length, unit: '회' },
        { label: '총 면담 시간', value: completed.reduce((sum, item) => sum + (Number(item.durationMinutes) || 0), 0), unit: '분' },
        { label: '만난 내담자', value: new Set(activities.map(item => item.clientId)).size, unit: '명' },
    ];
}

// 필터용 내담자 목록(중복 제거)을 만든다.
function clientOptions(activities) {
    const clients = new Map();
    activities.forEach(item => { if (!clients.has(item.clientId)) clients.set(item.clientId, item.clientName); });
    return [...clients];
}

// AI 내담자 면담 활동 이력 화면: 요약 지표, 내담자 필터, 면담별 대화록·사례개념화·피드백 리포트를 보여준다.
export default function ActivitySection() {
    const [activities, setActivities] = useState(null);
    const [error, setError] = useState('');
    const [clientId, setClientId] = useState('all');
    const [openId, setOpenId] = useState(null);

    // 활동 목록을 불러와 화면용 형태로 넣는다.
    const load = useCallback(async () => {
        setError('');
        setActivities(null);
        try {
            setActivities(toActivities(await getAiClientActivity()));
        } catch (loadError) {
            setError(loadError.message);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // 같은 면담을 다시 누르면 대화록을 접고, 다른 면담을 누르면 그 면담만 펼친다.
    function toggleDialogue(instanceId) {
        setOpenId(previous => previous === instanceId ? null : instanceId);
    }

    const heading = <div className="my-page-section-heading"><div><h2>AI 내담자 활동 이력</h2><p>AI 내담자와 진행한 면담과 피드백을 확인하세요.</p></div><Link className="my-page-button primary" to="/ai-clients">새 면담 시작</Link></div>;
    if (error) return <>{heading}<div className="my-page-card my-page-placeholder" role="alert"><h3>활동 이력을 불러오지 못했습니다</h3><p>{error}</p><button type="button" className="my-page-button my-page-retry" onClick={load}>다시 시도</button></div></>;
    if (!activities) return <>{heading}<p className="my-page-notice" role="status">활동 이력을 불러오고 있습니다.</p></>;

    const filtered = activities.filter(item => clientId === 'all' || item.clientId === clientId);

    return <>
        {heading}
        <div className="my-page-card my-page-metrics">
            {summarize(activities).map(item => <div key={item.label}><strong>{item.value}<small>{item.unit}</small></strong><span>{item.label}</span></div>)}
        </div>
        {activities.length > 0 && <div className="my-page-filter"><label htmlFor="my-page-case">내담자</label><select id="my-page-case" value={clientId} onChange={event => setClientId(event.target.value)}><option value="all">전체</option>{clientOptions(activities).map(([value, name]) => <option key={value} value={value}>{name}</option>)}</select><span>{filtered.length}건</span></div>}
        {filtered.length === 0 ? <div className="my-page-card my-page-placeholder"><h3>아직 면담 기록이 없습니다</h3><p>AI 내담자와 첫 면담을 시작해보세요.</p></div> : <ul className="my-page-card my-page-list">
            {filtered.map(item => {
                const open = openId === item.instanceId;
                const dialogueId = `dialogue-${item.instanceId}`;
                const completed = item.status === 'completed';
                return <li key={item.instanceId} className={open ? 'expanded' : undefined}>
                    <div className="my-page-list-title">
                        <div><span className="my-page-meta">{[formatDate(item.date), item.durationMinutes && `${item.durationMinutes}분`, item.title].filter(Boolean).join(' · ')}</span><h3>{item.clientName}<small className={completed ? 'done' : undefined}>{statusLabels[item.status] || item.status || '—'}</small></h3></div>
                        <div className="my-page-list-actions">
                            <button type="button" className="my-page-button" aria-expanded={open} aria-controls={dialogueId} onClick={() => toggleDialogue(item.instanceId)}>{open ? '대화록 접기' : '대화록'}</button>
                            {completed && <Link className="my-page-button" to={`/mypage/activity/${encodeURIComponent(item.instanceId)}/conceptualization`} state={{ activity: item }}>사례개념화</Link>}
                            {completed && <Link className="my-page-button" to={`/mypage/activity/${encodeURIComponent(item.instanceId)}/feedback`} state={{ activity: item }}>피드백 리포트</Link>}
                        </div>
                    </div>
                    {open && <ChatTranscript id={dialogueId} instanceId={item.instanceId} clientName={item.clientName} />}
                </li>;
            })}
        </ul>}
        <div className="my-page-card my-page-placeholder"><h3>성장 추이 <small>준비 중</small></h3><p>회기별 OARS 사용 비율과 발화 비율 변화를 그래프로 볼 수 있도록 준비하고 있습니다.</p></div>
    </>;
}
